// apps/patient/src/pages/appointments/ManageAppointmentPage.tsx
// Public cancel / reschedule page reached from a reminder link (/a/:token).
// Standalone (no PatientLayout, no auth). The token is only ever passed to the API.

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AlertCircle, CalendarClock, Clock, Loader2, MapPin, Phone, User, XCircle } from 'lucide-react';
import { formatInTimeZone } from 'date-fns-tz';
import { patientApiService } from '../../services/api';
import { ManageAppointmentInfo, formatLocationAddress, formatProviderName, readErrorMessage } from '../../components/appointments/manageTypes';
import { ManageWindowNotice } from '../../components/appointments/ManageWindowNotice';
import { CancelAppointmentDialog } from '../../components/appointments/CancelAppointmentDialog';
import { RescheduleAppointmentPanel } from '../../components/appointments/RescheduleAppointmentPanel';

const INVALID_LINK_MESSAGE = 'This link is no longer valid. Please contact the office.';

type View = 'summary' | 'reschedule' | 'cancelled';

export function ManageAppointmentPage() {
  const { token } = useParams<{ token: string }>();
  const [info, setInfo] = useState<ManageAppointmentInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<View>('summary');
  const [showCancel, setShowCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError(INVALID_LINK_MESSAGE);
      setLoading(false);
      return;
    }
    let active = true;
    (async () => {
      try {
        const result = await patientApiService.getAppointmentLink(token);
        if (active) setInfo(result);
      } catch {
        if (active) setError(INVALID_LINK_MESSAGE);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [token]);

  const handleCancel = async (reason: string) => {
    if (!token) return;
    setCancelling(true);
    setCancelError(null);
    try {
      await patientApiService.cancelAppointmentByLink(token, reason || null);
      setShowCancel(false);
      setView('cancelled');
    } catch (err) {
      setCancelError(await readErrorMessage(err, 'We could not cancel your appointment. Please try again or call the office.'));
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-3" />
          <p className="text-gray-600">Loading your appointment...</p>
        </div>
      </div>
    );
  }

  if (error || !info) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md text-center p-8">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Unable to Load Appointment</h1>
          <p className="text-gray-600">{error || INVALID_LINK_MESSAGE}</p>
        </div>
      </div>
    );
  }

  const startDate = new Date(info.start_timestamp);
  const dateText = formatInTimeZone(startDate, info.timezone, 'EEEE, MMMM d, yyyy');
  const timeText = formatInTimeZone(startDate, info.timezone, 'h:mm a zzz');
  const providerName = formatProviderName(info.provider);
  const phone = info.organization_phone;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-lg mx-auto px-4 py-8 space-y-4">
        <div className="text-center">
          <p className="text-sm text-gray-500">{info.organization_name}</p>
          <h1 className="text-xl font-semibold text-gray-900 mt-1">
            {view === 'reschedule' ? 'Reschedule Appointment' : 'Your Appointment'}
          </h1>
        </div>

        {view === 'cancelled' && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <XCircle className="h-10 w-10 text-red-500 mx-auto mb-3" />
            <p className="text-red-900 font-medium">
              Your appointment on {dateText} at {timeText} has been cancelled.
            </p>
            <p className="text-sm text-red-800 mt-3">
              Need to book again? Call {info.organization_name}
              {phone && (
                <>
                  {' '}at <a href={`tel:${phone}`} className="underline font-medium">{phone}</a>
                </>
              )}.
            </p>
          </div>
        )}

        {view === 'reschedule' && (
          <>
            <RescheduleAppointmentPanel
              info={info}
              onSubmit={(body) => patientApiService.rescheduleAppointmentByLink(token!, body)}
              onJoinWaitlist={(body) => patientApiService.joinWaitlistByLink(token!, body)}
            />
            <button
              type="button"
              onClick={() => setView('summary')}
              className="w-full px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Back
            </button>
          </>
        )}

        {view === 'summary' && (
          <>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-3">
              <div className="flex items-center gap-3">
                <CalendarClock className="h-5 w-5 text-gray-400" />
                <span className="font-medium text-gray-900">{dateText}</span>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-gray-400" />
                <span className="text-gray-800">{timeText}</span>
              </div>
              <div className="flex items-center gap-3">
                <User className="h-5 w-5 text-gray-400" />
                <span className="text-gray-800">{providerName}</span>
              </div>
              {info.is_telehealth ? (
                <div className="flex items-center gap-3">
                  <MapPin className="h-5 w-5 text-gray-400" />
                  <span className="text-gray-800">Video visit</span>
                </div>
              ) : info.location && (
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-gray-800">{info.location.name}</p>
                    {formatLocationAddress(info.location.address) && (
                      <p className="text-sm text-gray-500">{formatLocationAddress(info.location.address)}</p>
                    )}
                  </div>
                </div>
              )}
              {phone && (
                <div className="flex items-center gap-3">
                  <Phone className="h-5 w-5 text-gray-400" />
                  <a href={`tel:${phone}`} className="text-primary hover:underline">{phone}</a>
                </div>
              )}
            </div>

            <div className="space-y-3">
              {info.can_reschedule && (
                <button
                  type="button"
                  onClick={() => setView('reschedule')}
                  className="w-full px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Reschedule Appointment
                </button>
              )}
              {info.can_cancel && (
                <button
                  type="button"
                  onClick={() => { setCancelError(null); setShowCancel(true); }}
                  className="w-full px-4 py-3 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors"
                >
                  Cancel Appointment
                </button>
              )}
              {/* Window notice only when the appointment itself can no longer be changed
                  or its type is not offered online; an action the org simply does not
                  offer online is hidden silently. */}
              {(info.blocked_by_type || info.can_manage === false || (info.can_manage === undefined && (!info.can_cancel || !info.can_reschedule))) && (
                <ManageWindowNotice windowHours={info.cancellation_window_hours} phone={phone} blockedByType={info.blocked_by_type} />
              )}
            </div>
          </>
        )}
      </div>

      <CancelAppointmentDialog
        open={showCancel}
        cancelling={cancelling}
        error={cancelError}
        onClose={() => setShowCancel(false)}
        onConfirm={handleCancel}
      />
    </div>
  );
}
